import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { authenticatedFetch } from '../../../lib/api';
import { useToast } from '../../../components/shared/Toast';
import { erpPageBtnPrimaryClass, erpPageBtnSecondaryClass } from '@euroasiann/shared/ui';
import { pmFieldClass, pmLabelClass, pmTextareaClass } from '../port-management.ui';

const FACILITY_TYPES = ['Terminal', 'Berth', 'Jetty', 'Dry Dock', 'Anchorage'];
const CARGO_TYPES = ['Container', 'Bulk', 'Oil', 'Gas', 'General', 'Ro-Ro'];

interface TerminalFormProps {
    initialData?: any;
    onSuccess: () => void;
    onCancel: () => void;
}

export function TerminalForm({ initialData, onSuccess, onCancel }: TerminalFormProps) {
    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
        defaultValues: initialData ? {
            ...initialData,
            portId: initialData.portId?._id || initialData.portId,
            allowedVesselTypes: initialData.allowedVesselTypes?.join(', ') || '',
            facilityType: initialData.facilityType || 'Terminal',
        } : {
            name: '',
            code: '',
            portId: '',
            facilityType: 'Terminal',
            cargoType: 'General',
            allowedVesselTypes: '',
            operatingHours: '',
            entryInstructions: '',
            maxDraft: '',
            maxLOA: '',
            berthCount: 1,
            status: 'Active',
        },
    });
    const toast = useToast();

    const { data: ports = [] } = useQuery({
        queryKey: ['ports-dropdown'],
        queryFn: async () => {
            const res = await authenticatedFetch('/api/v1/ports?status=Active&limit=1000');
            const data = await res.json();
            return data.data || [];
        },
    });

    const mutation = useMutation({
        mutationFn: async (data: any) => {
            const url = initialData ? `/api/v1/terminals/${initialData._id}` : '/api/v1/terminals';
            const method = initialData ? 'PUT' : 'POST';

            const payload = {
                ...data,
                maxDraft: data.maxDraft ? Number(data.maxDraft) : undefined,
                maxLOA: data.maxLOA ? Number(data.maxLOA) : undefined,
                berthCount: data.berthCount ? Number(data.berthCount) : 1,
                allowedVesselTypes: data.allowedVesselTypes
                    ? String(data.allowedVesselTypes).split(',').map((t: string) => t.trim()).filter(Boolean)
                    : [],
            };

            const res = await authenticatedFetch(url, {
                method,
                body: JSON.stringify(payload),
                headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to save terminal');
            }
            return res.json();
        },
        onSuccess: () => {
            toast.success(initialData ? 'Facility updated' : 'Facility created');
            onSuccess();
        },
        onError: (err: any) => {
            toast.error(err.message);
        },
    });

    return (
        <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={pmLabelClass}>Name *</label>
                    <input
                        {...register('name', { required: 'Name is required' })}
                        className={pmFieldClass}
                    />
                    {errors.name && <span className="text-red-500 text-xs">{errors.name.message as string}</span>}
                </div>
                <div>
                    <label className={pmLabelClass}>Code</label>
                    <input {...register('code')} className={pmFieldClass} />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={pmLabelClass}>Facility Type *</label>
                    <select {...register('facilityType')} className={pmFieldClass}>
                        {FACILITY_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={pmLabelClass}>Cargo Type</label>
                    <select {...register('cargoType')} className={pmFieldClass}>
                        {CARGO_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div>
                <label className={pmLabelClass}>Port *</label>
                <select
                    {...register('portId', { required: 'Port is required' })}
                    className={pmFieldClass}
                >
                    <option value="">Select Port</option>
                    {ports.map((port: any) => (
                        <option key={port._id} value={port._id}>
                            {port.name} ({port.code})
                        </option>
                    ))}
                </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
                <div>
                    <label className={pmLabelClass}>Max Draft (m)</label>
                    <input type="number" step="any" {...register('maxDraft')} className={pmFieldClass} />
                </div>
                <div>
                    <label className={pmLabelClass}>Max LOA (m)</label>
                    <input type="number" step="any" {...register('maxLOA')} className={pmFieldClass} />
                </div>
                <div>
                    <label className={pmLabelClass}>Berth Count</label>
                    <input type="number" {...register('berthCount')} className={pmFieldClass} />
                </div>
            </div>

            <div>
                <label className={pmLabelClass}>Allowed Vessel Types (comma separated)</label>
                <input
                    {...register('allowedVesselTypes')}
                    placeholder="Container, Bulk Carrier, Tanker"
                    className={pmFieldClass}
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={pmLabelClass}>Operating Hours</label>
                    <input {...register('operatingHours')} placeholder="24/7" className={pmFieldClass} />
                </div>
                <div>
                    <label className={pmLabelClass}>Status</label>
                    <select
                        {...register('status')}
                        className={pmFieldClass}
                    >
                        <option value="Active" className="bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">Active</option>
                        <option value="Under Maintenance" className="bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">Under Maintenance</option>
                        <option value="Inactive" className="bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">Inactive</option>
                    </select>
                </div>
            </div>

            <div>
                <label className={pmLabelClass}>Entry Instructions</label>
                <textarea {...register('entryInstructions')} rows={3} className={pmTextareaClass} />
            </div>

            <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={onCancel} className={erpPageBtnSecondaryClass}>
                    Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className={erpPageBtnPrimaryClass}>
                    {isSubmitting ? 'Saving...' : 'Save'}
                </button>
            </div>
        </form>
    );
}
